// Global client-error reporting. Crashes and unhandled errors are POSTed to
// the conference backend's /api/client-error (which logs + forwards to the
// shared error mailer server-side — no CORS exposure, works pre-auth).
//
// Guards exist on both sides of the request. The browser deduplicates a crash
// in memory and in sessionStorage (so a reload loop cannot mail it repeatedly),
// and caps distinct reports. The backend independently rate-limits by IP.

const PRODUCTION_APP_HOST = 'conference.onkospace.de'
const PRODUCTION_API_ORIGIN = 'https://conference-api.onkospace.de'
const MAX_REPORTS_PER_LOAD = 5
const MAX_REPORTS_PER_SESSION_WINDOW = 5
const SESSION_WINDOW_MS = 10 * 60 * 1000
const SESSION_LEDGER_KEY = 'cc:client-error-ledger:v1'

let defaultReporter = null
let installedHandlers = null

function safeString (value, fallback = '') {
  try {
    if (value == null) return fallback
    return typeof value === 'string' ? value : String(value)
  } catch {
    return fallback
  }
}

function errorMessage (error) {
  try {
    if (error?.message) return safeString(error.message, 'unknown')
  } catch {}
  if (typeof error === 'string') return error
  try {
    const json = JSON.stringify(error)
    if (json && json !== '{}') return json
  } catch {}
  return safeString(error, 'unknown') || 'unknown'
}

// Crash text can contain a request URL. Keep stacks useful while stripping the
// conference route (which may also contain a personal invite identifier),
// secrets in query-like text, bearer tokens, and JWT-shaped values.
export function redactClientErrorText (value, max = 6000) {
  const text = safeString(value)
    .replace(/(\/(?:api\/)?conference\/)[0-9a-f-]{36}(?:\/[^\s?#]*)?/gi, '$1[redacted]')
    .replace(/([?&](?:access_token|token|auth|key|secret|password|resume_secret|participant_token)=)[^&#\s]*/gi, '$1[redacted]')
    .replace(/\bBearer\s+[^\s,;]+/gi, 'Bearer [redacted]')
    .replace(/\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g, '[redacted-jwt]')
  return text.slice(0, max)
}

function shortFingerprint (value) {
  // FNV-1a: stable and deliberately non-reversible enough for a small local
  // dedupe key. The actual diagnostic text is never stored in sessionStorage.
  let hash = 0x811c9dc5
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(36)
}

function reserveSessionFingerprint (storage, key, now) {
  if (!storage?.getItem || !storage?.setItem) return true
  let parsed = []
  try {
    const raw = storage.getItem(SESSION_LEDGER_KEY)
    if (raw) {
      try { parsed = JSON.parse(raw) } catch { parsed = [] }
    }
    const ledger = Array.isArray(parsed)
      ? parsed.filter(entry => entry && typeof entry.key === 'string' && Number.isFinite(entry.at) && entry.at <= now && now - entry.at < SESSION_WINDOW_MS)
      : []
    if (ledger.some(entry => entry.key === key)) return false
    if (ledger.length >= MAX_REPORTS_PER_SESSION_WINDOW) return false
    ledger.push({ key, at: now })
    storage.setItem(SESSION_LEDGER_KEY, JSON.stringify(ledger))
    return true
  } catch {
    // Storage can be disabled in private/restricted browser modes. The
    // in-memory guards still apply, so reporting remains safe and useful.
    return true
  }
}

function roleForPath (pathname) {
  const path = safeString(pathname)
  if (/admin/i.test(path)) return 'admin'
  if (/conference/i.test(path)) return 'participant'
  return 'unknown'
}

function endpointForLocation (locationLike, env = {}) {
  const hostname = safeString(locationLike?.hostname).toLowerCase()
  const origin = safeString(locationLike?.origin)
  const configured = safeString(env.REACT_APP_CONFERENCE_API_URL).trim()
  if (configured) {
    try { return `${new URL(configured, origin).origin}/api/client-error` } catch {}
  }
  if (hostname === PRODUCTION_APP_HOST) return `${PRODUCTION_API_ORIGIN}/api/client-error`
  return `${origin}/api/client-error`
}

function isLoopback (hostname) {
  const host = safeString(hostname).toLowerCase()
  return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]'
}

export function createClientErrorReporter ({
  location: locationLike,
  navigator: navigatorLike,
  fetch: fetchImpl,
  sessionStorage: storage,
  now = () => Date.now(),
  env = typeof process !== 'undefined' ? process.env : {},
} = {}) {
  let sent = 0
  const seen = new Set()
  const seenErrorObjects = new WeakSet()

  return function report (where, error, info) {
    try {
      if (!locationLike || isLoopback(locationLike.hostname) || typeof fetchImpl !== 'function') return false

      const rawMessage = errorMessage(error)
      const message = redactClientErrorText(rawMessage, 1500) || 'unknown'
      const safeWhere = redactClientErrorText(where || 'unknown', 120) || 'unknown'
      // Dynamic UUIDs/timestamps must not defeat crash-loop dedupe. `where`
      // plus the normalized message still distinguishes independent failures.
      const normalized = `${safeWhere}|${message}`
        .replace(/[0-9a-f-]{36}/gi, '[uuid]')
        .replace(/\b\d{6,}\b/g, '[number]')
      const fingerprint = shortFingerprint(normalized.slice(0, 1200))

      // React and window.onerror can expose the very same Error object to two
      // safety nets. Preserve the first (most immediate) report and suppress
      // the duplicate even though its `where` label differs.
      if (error && (typeof error === 'object' || typeof error === 'function')) {
        if (seenErrorObjects.has(error)) return false
        seenErrorObjects.add(error)
      }
      if (seen.has(fingerprint) || sent >= MAX_REPORTS_PER_LOAD) return false
      seen.add(fingerprint)
      if (!reserveSessionFingerprint(storage, fingerprint, now())) return false
      sent += 1

      // Chrome/Android auto-translate detaches DOM nodes under React and
      // throws this exact class of error. Tag it so the alert is actionable.
      const translated = /removeChild|insertBefore/.test(message)
        ? ' (möglicherweise Browser-Übersetzung aktiv)'
        : ''
      const origin = safeString(locationLike.origin)
      const pathname = redactClientErrorText(locationLike.pathname, 500)
      const payload = {
        where: `${safeWhere}${translated}`,
        message,
        stack: redactClientErrorText(error?.stack, 6000),
        component_stack: redactClientErrorText(info?.componentStack, 6000),
        // Deliberately omit query strings and fragments.
        url: redactClientErrorText(`${origin}${pathname}`, 500),
        ua: redactClientErrorText(navigatorLike?.userAgent, 400),
        role: roleForPath(locationLike.pathname),
      }

      // No retry and an attached rejection handler are intentional: a failed
      // reporting request must never create an unhandled-rejection report or
      // recursive email loop. keepalive gives a last-moment crash/reload a
      // chance to reach the backend without adding a second delivery path.
      const delivery = fetchImpl(endpointForLocation(locationLike, env), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      })
      Promise.resolve(delivery).catch(() => {})
      return true
    } catch {
      // Reporting is the last line of defense and must never throw.
      return false
    }
  }
}

function browserReporter () {
  if (!defaultReporter && typeof window !== 'undefined') {
    defaultReporter = createClientErrorReporter({
      location: window.location,
      navigator: typeof navigator !== 'undefined' ? navigator : null,
      fetch: typeof fetch === 'function' ? fetch.bind(globalThis) : null,
      sessionStorage: (() => { try { return window.sessionStorage } catch { return null } })(),
    })
  }
  return defaultReporter
}

export function reportClientError (where, error, info) {
  return browserReporter()?.(where, error, info) || false
}

// window-level safety net for errors OUTSIDE React's render tree. Noise
// filters cover opaque cross-origin errors, observer warnings and expected
// network/media cancellation paths already surfaced through UI recovery.
export function isClientErrorNoise (message) {
  return /^(?:Script error\.?|ResizeObserver loop|Failed to fetch|NetworkError|Load failed|AbortError\b|The play\(\) request was interrupted)/i.test(safeString(message))
}

export function installGlobalErrorHandlers (report = reportClientError, target = typeof window !== 'undefined' ? window : null) {
  if (!target?.addEventListener) return () => {}
  if (installedHandlers?.target === target) return installedHandlers.remove
  installedHandlers?.remove?.()

  const onError = (event) => {
    const message = safeString(event?.message)
    // Resource/cross-origin error events can contain neither a useful message
    // nor an Error object. Do not consume the report budget for empty noise.
    if ((!message && !event?.error) || event?.error?.name === 'AbortError' || isClientErrorNoise(message)) return
    report('window.onerror', event?.error || new Error(message))
  }
  const onUnhandledRejection = (event) => {
    const reason = event?.reason
    const message = errorMessage(reason)
    if (!message || reason?.name === 'AbortError' || isClientErrorNoise(message)) return
    report('unhandledrejection', reason instanceof Error ? reason : new Error(message))
  }
  target.addEventListener('error', onError)
  target.addEventListener('unhandledrejection', onUnhandledRejection)
  const remove = () => {
    target.removeEventListener('error', onError)
    target.removeEventListener('unhandledrejection', onUnhandledRejection)
    if (installedHandlers?.target === target) installedHandlers = null
  }
  installedHandlers = { target, remove }
  return remove
}

// Test isolation only. Production never resets delivery guards during a page
// lifetime, which is what makes a render/reload loop non-spammy.
export function resetClientErrorReportingForTests () {
  installedHandlers?.remove?.()
  installedHandlers = null
  defaultReporter = null
}
